import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/auth.store';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Server, Activity, MessageCircle, DollarSign, Plus, ArrowRight, Rocket, Coins, AlertTriangle, HardDrive, Cloud, Zap, Globe, Key } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { motion } from 'framer-motion';

export function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuthStore();

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['user-stats'],
    queryFn: async () => {
      const { data } = await api.get('/users/stats');
      return data;
    },
  });

  const { data: instancesData, isLoading: instancesLoading, isFetching: instancesFetching } = useQuery({
    queryKey: ['instances'],
    queryFn: async () => {
      const { data } = await api.get('/instances?limit=5');
      return data;
    },
  });

  // Show skeleton when fetching AND no cached data exists yet
  const shouldShowSkeleton = (instancesLoading && !instancesData) || (instancesFetching && !instancesData);

  const hasPlatformKeyInstances = instancesData?.instances?.some((i: any) => i.key_mode === 'platform_key');

  const { data: walletBalance } = useQuery({
    queryKey: ['wallet-balance'],
    queryFn: async () => {
      const { data } = await api.get('/wallet/balance');
      return data;
    },
    enabled: !!hasPlatformKeyInstances,
  });

  const isLowBalance = walletBalance && walletBalance.balance_cents > 0 && walletBalance.balance_cents <= (walletBalance.low_balance_threshold || 100);
  const isZeroBalance = walletBalance && walletBalance.balance_cents === 0 && hasPlatformKeyInstances;

  const totalMonthlyCents = instancesData?.instances?.reduce(
    (sum: number, i: any) => sum + (parseInt(i.monthlyCostCents || i.monthly_cost_cents) || 0),
    0,
  ) || 0;

  const managedCount = instancesData?.instances?.filter((i: any) => i.hosting_type === 'managed' || i.hostingType === 'managed').length || 0;
  const byosCount = instancesData?.instances?.filter((i: any) => i.hosting_type === 'byos' || i.hostingType === 'byos').length || 0;

  const statCards = [
    {
      title: t('dashboard.stats.totalInstances'),
      value: stats?.totalInstances || 0,
      subtitle: `${managedCount} Managed, ${byosCount} BYOS`,
      icon: Server,
      color: 'text-emerald-500 bg-emerald-500/10',
    },
    {
      title: t('dashboard.stats.activeInstances'),
      value: stats?.activeInstances || 0,
      subtitle: stats?.activeInstances === stats?.totalInstances ? 'All running' : `${stats?.totalInstances - stats?.activeInstances} stopped`,
      icon: Activity,
      color: 'text-blue-500 bg-blue-500/10',
    },
    {
      title: t('dashboard.stats.totalMessages'),
      value: stats?.totalMessages || '0',
      subtitle: 'This month',
      icon: MessageCircle,
      color: 'text-purple-500 bg-purple-500/10',
    },
    {
      title: t('dashboard.stats.monthlySpend'),
      value: `$${(totalMonthlyCents / 100).toFixed(2)}`,
      subtitle: totalMonthlyCents > 0 ? 'Recurring billing' : 'No subscriptions',
      icon: DollarSign,
      color: 'text-orange-500 bg-orange-500/10',
    },
  ];

  const statusColors: Record<string, string> = {
    running: 'bg-emerald-500',
    stopped: 'bg-gray-400',
    provisioning: 'bg-yellow-500',
    error: 'bg-red-500',
    starting: 'bg-blue-500',
    stopping: 'bg-orange-500',
    paused: 'bg-amber-500',
  };

  return (
    <div className="space-y-8">
      {/* Welcome */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t('dashboard.welcome')}, {user?.name?.split(' ')[0]}!</h1>
          <p className="text-muted-foreground">{t('dashboard.overview')}</p>
        </div>
        <Link to="/dashboard/new">
          <Button className="gap-2">
            <Plus className="h-4 w-4" />
            {t('dashboard.newInstance')}
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statsLoading || instancesLoading ? (
          // Skeleton loaders for stats
          Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-8 w-8 rounded-lg" />
                </div>
                <Skeleton className="h-8 w-16 mb-1" />
                <Skeleton className="h-3 w-32" />
              </CardContent>
            </Card>
          ))
        ) : (
          statCards.map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1, duration: 0.3 }}
            >
              <Card className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm text-muted-foreground">{stat.title}</p>
                    <div className={`p-2 rounded-lg ${stat.color}`}>
                      <stat.icon className="h-4 w-4" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold mb-1">{stat.value}</p>
                  {stat.subtitle && (
                    <p className="text-xs text-muted-foreground">{stat.subtitle}</p>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))
        )}
      </div>

      {/* Wallet Balance Card */}
      {hasPlatformKeyInstances && walletBalance && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
        >
          <Card className={cn(
            'border',
            isZeroBalance ? 'border-red-500/30 bg-red-500/5' :
            isLowBalance ? 'border-amber-500/30 bg-amber-500/5' :
            'border-emerald-500/20 bg-emerald-500/5',
          )}>
            <CardContent className="p-5 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={cn(
                  'h-10 w-10 rounded-full flex items-center justify-center',
                  isZeroBalance || isLowBalance ? 'bg-amber-500' : 'gradient-primary',
                )}>
                  {isZeroBalance || isLowBalance
                    ? <AlertTriangle className="h-5 w-5 text-white" />
                    : <Coins className="h-5 w-5 text-white" />
                  }
                </div>
                <div>
                  <p className="font-semibold text-lg">${((walletBalance.balance_cents || 0) / 100).toFixed(2)}</p>
                  <p className="text-sm text-muted-foreground">
                    {isZeroBalance
                      ? t('wallet.noFunds')
                      : isLowBalance
                        ? t('wallet.lowBalance')
                        : t('wallet.balance')
                    }
                  </p>
                </div>
              </div>
              <Link to="/dashboard/billing?tab=wallet">
                <Button variant={isZeroBalance || isLowBalance ? 'default' : 'outline'} size="sm" className="gap-2">
                  <Coins className="h-4 w-4" />
                  {t('wallet.addFunds')}
                </Button>
              </Link>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Quick Actions */}
      {!instancesData?.instances?.length && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="border-dashed border-2 border-primary/20 bg-primary/5">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="h-20 w-20 rounded-full gradient-primary flex items-center justify-center mb-6">
                <Rocket className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">{t('dashboard.noInstances')}</h3>
              <p className="text-sm text-muted-foreground mb-6 max-w-md text-center">
                {t('dashboard.noInstancesDescription')}
              </p>
              <Link to="/dashboard/new">
                <Button size="lg" className="gap-2">
                  <Rocket className="h-4 w-4" />
                  {t('dashboard.createFirst')}
                </Button>
              </Link>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Instances */}
      {shouldShowSkeleton ? (
        // Skeleton loader for instances
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-4 w-16" />
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center justify-between p-4 rounded-xl border-2">
                  <div className="flex items-center gap-4 flex-1">
                    <Skeleton className="h-12 w-12 rounded-xl" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Skeleton className="h-5 w-32" />
                        <Skeleton className="h-5 w-16 rounded-full" />
                      </div>
                      <Skeleton className="h-3 w-48 mb-1" />
                      <Skeleton className="h-3 w-40" />
                    </div>
                  </div>
                  <div className="hidden sm:block">
                    <Skeleton className="h-4 w-16 mb-1" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : instancesData?.instances?.length > 0 ? (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">{t('dashboard.instances')}</CardTitle>
            <Link to="/dashboard/instances" className="text-sm text-primary hover:underline flex items-center gap-1">
              {t('common.viewAll')}
              <ArrowRight className="h-3 w-3" />
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {instancesData.instances.map((instance: any, index: number) => {
                const hostingType = instance.hostingType || instance.hosting_type;
                const keyMode = instance.keyMode || instance.key_mode;
                const hostingIcon = hostingType === 'byos' ? HardDrive : Cloud;
                const keyModeIcon = keyMode === 'own_key' ? Key : Zap;

                return (
                  <motion.div
                    key={instance.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link
                      to={`/dashboard/instances/${instance.id}`}
                      className="flex items-center justify-between p-4 rounded-xl border-2 hover:bg-accent/50 hover:border-primary/30 hover:shadow-md transition-all group"
                    >
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        {/* Status Indicator */}
                        <div className="relative flex-shrink-0">
                          <div className={cn(
                            'h-12 w-12 rounded-xl flex items-center justify-center',
                            instance.status === 'running' ? 'bg-emerald-500/10' :
                            instance.status === 'error' ? 'bg-red-500/10' :
                            'bg-gray-500/10'
                          )}>
                            <Server className={cn(
                              'h-6 w-6',
                              instance.status === 'running' ? 'text-emerald-500' :
                              instance.status === 'error' ? 'text-red-500' :
                              'text-gray-500'
                            )} />
                          </div>
                          <div className={cn(
                            'absolute -bottom-1 -right-1 h-4 w-4 rounded-full border-2 border-background flex items-center justify-center',
                            statusColors[instance.status] || 'bg-gray-400'
                          )}>
                            {instance.status === 'running' && (
                              <div className={cn('absolute inset-0 rounded-full animate-ping opacity-40', statusColors[instance.status])} />
                            )}
                          </div>
                        </div>

                        {/* Instance Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1.5">
                            <p className="font-bold text-base truncate">{instance.name}</p>
                            <Badge
                              variant={
                                instance.status === 'running' ? 'success' :
                                instance.status === 'error' ? 'destructive' :
                                'secondary'
                              }
                              className="capitalize text-xs font-semibold"
                            >
                              {t(`dashboard.instance.${instance.status}` as any) || instance.status}
                            </Badge>
                          </div>

                          {/* First Row: Hosting & Key Mode */}
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mb-1">
                            <div className="flex items-center gap-1.5">
                              {React.createElement(hostingIcon, { className: 'h-3.5 w-3.5' })}
                              <span className="font-medium">{hostingType === 'managed' ? 'Managed' : 'BYOS'}</span>
                            </div>
                            <span>•</span>
                            <div className="flex items-center gap-1.5">
                              {React.createElement(keyModeIcon, { className: 'h-3.5 w-3.5' })}
                              <span>{keyMode === 'platform_key' ? 'Platform Key' : 'Own Key'}</span>
                            </div>
                            {instance.region && instance.region !== 'shared' && (
                              <>
                                <span className="hidden lg:inline">•</span>
                                <span className="hidden lg:inline">{instance.region}</span>
                              </>
                            )}
                          </div>

                          {/* Second Row: AI Model & Channels */}
                          <div className="flex items-center gap-3 text-xs">
                            {(instance.aiModel || instance.ai_model) && (
                              <div className="flex items-center gap-1.5">
                                <div className="h-1.5 w-1.5 rounded-full bg-purple-500" />
                                <span className="text-muted-foreground font-mono text-[10px]">
                                  {(instance.aiModel || instance.ai_model).replace('openrouter/', '').replace('openai/', '')}
                                </span>
                              </div>
                            )}
                            {instance.channels && instance.channels.length > 0 && (
                              <div className="flex items-center gap-1">
                                {instance.channels.map((channel: string) => (
                                  <Badge key={channel} variant="outline" className="text-[10px] px-1.5 py-0 h-5">
                                    {channel}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Right side: Cost, URL & Actions */}
                      <div className="flex items-center gap-4 flex-shrink-0">
                        <div className="text-right hidden sm:block">
                          {/* Cost */}
                          <div className="mb-1">
                            {hostingType === 'byos' ? (
                              <p className="font-bold text-sm text-emerald-600">One-time $5</p>
                            ) : (
                              <div>
                                <p className="font-bold text-sm">
                                  ${(parseInt(instance.monthlyCostCents || instance.monthly_cost_cents) || 0) / 100}
                                  <span className="text-xs text-muted-foreground font-normal">/mo</span>
                                </p>
                                {keyMode === 'platform_key' && (
                                  <p className="text-[10px] text-amber-600 font-medium">+ wallet usage</p>
                                )}
                              </div>
                            )}
                          </div>

                          {/* URL or IP */}
                          {(instance.openclawUrl || instance.openclaw_url || instance.server_ip) && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1 justify-end">
                              <Globe className="h-3 w-3" />
                              <span className="truncate max-w-[150px]">
                                {instance.openclawUrl?.replace('https://', '').replace('http://', '') ||
                                 instance.openclaw_url?.replace('https://', '').replace('http://', '') ||
                                 instance.server_ip}
                              </span>
                            </p>
                          )}
                        </div>
                        <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Quick Start Guide */}
      {instancesData?.instances?.length > 0 && instancesData.instances.length < 3 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('dashboard.quickStart.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-accent/50">
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-sm font-bold text-primary">1</span>
                </div>
                <div>
                  <p className="font-medium text-sm">{t('dashboard.quickStart.step1Title')}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t('dashboard.quickStart.step1Description')}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-accent/50">
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-sm font-bold text-primary">2</span>
                </div>
                <div>
                  <p className="font-medium text-sm">{t('dashboard.quickStart.step2Title')}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t('dashboard.quickStart.step2Description')}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-accent/50">
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-sm font-bold text-primary">3</span>
                </div>
                <div>
                  <p className="font-medium text-sm">{t('dashboard.quickStart.step3Title')}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t('dashboard.quickStart.step3Description')}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
